import axios, { type Axios } from 'axios';
import type {
  AuthenticationDetails,
  Credentials,
  DeliveryMethod,
  Hub,
  PasswordlessAuthClient,
  PromiseOfOptional,
  SessionClient,
  StorageClient,
} from '../interfaces';
import { AuthSessionClient } from './AuthSessionClient';
import { AuthTokenName, BaseAuthClient } from './BaseAuthClient';
import { AuthZeroError } from './error';
import { inMemoryStorageClient } from './InMemoryStorageClient';

export enum ConnectionSendType {
  CODE = 'code',
}

export enum GrantType {
  OneTimePin = 'http://auth0.com/oauth/grant-type/passwordless/otp',
  RefreshToken = 'refresh_token',
}

const deliveryKeys: Record<DeliveryMethod, string> = {
  email: 'email',
  sms: 'phone_number',
};

export class PasswordlessAuthZeroClient<T extends BaseAuthUser = BaseAuthUser>
  extends BaseAuthClient<T>
  implements PasswordlessAuthClient<T>
{
  protected static instance: PasswordlessAuthClient<unknown>;
  private baseURL: string;

  /**
   * Retrieves or creates an instance of PasswordlessAuthClient if it wasn't previously
   * instantiated
   *
   * @param options - Options used to make HTTP Request to the Auth0 API
   * @param storageClient - Client used to store and retrieve data
   * @param sessionClient - CLient used to manage the session of the user
   *
   * @returns An instance of PasswordlessAuthClient
   */
  static getInstance<T extends BaseAuthUser = BaseAuthUser>(
    options: PasswordlessAuthZeroClientOptions,
    storageClient?: StorageClient,
    sessionClient?: SessionClient<T>,
    hub?: Hub,
  ): PasswordlessAuthClient<T> {
    if (!PasswordlessAuthZeroClient.instance) {
      const storage = storageClient ? storageClient : inMemoryStorageClient;
      const resolvedSessionClient = sessionClient
        ? sessionClient
        : AuthSessionClient;
      PasswordlessAuthZeroClient.instance = new PasswordlessAuthZeroClient(
        options,
        storage,
        resolvedSessionClient,
        hub,
      );
    }

    return PasswordlessAuthZeroClient.instance as PasswordlessAuthZeroClient<T>;
  }

  /**
   * A client used to make HTTP Requests
   */
  private httpClient: Axios;
  /**
   * The audience allowed to use the Auth0 API
   */
  private readonly audience: string;
  private readonly scope: string = 'openid profile email offline_access';
  protected constructor(
    private readonly options: PasswordlessAuthZeroClientOptions,
    storage: StorageClient,
    sessionClient: SessionClient<T>,
    hub?: Hub,
    autoRefresh = true,
  ) {
    super(
      storage,
      'PasswordlessAuthZeroClient',
      sessionClient,
      hub,
      autoRefresh,
    );
    const { domain } = options;
    this.httpClient = axios.create({
      baseURL: domain,
      headers: { 'content-type': 'application/json' },
    });

    this.baseURL = `https://${domain}`;

    this.audience = `${domain}/api/v2/`;
  }

  private get clientId(): string {
    return this.options.clientId;
  }

  async startAuthentication(
    mode: DeliveryMethod,
    username: string,
  ): Promise<AuthenticationDetails> {
    try {
      const key = deliveryKeys[mode];
      /**
       * TODO: Create a type used for this input have a look at the
       * link below for more information
       * https://auth0.com/docs/api/authentication#authenticate-user
       */
      const input = {
        [key]: username,
        connection: mode,
      };

      await this.httpClient.post('/passwordless/start', {
        client_id: this.clientId,
        send: ConnectionSendType.CODE,
        ...input,
      });

      const field = username.includes('@') ? 'email' : 'phone';

      return {
        username,
        field,
      };
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }

  async completeAuthentication(
    mode: DeliveryMethod,
    username: string,
    code: string,
  ): Promise<Credentials> {
    try {
      const { data } = await this.httpClient.post<AuthZeroTokenResponse>(
        '/oauth/token',
        {
          client_id: this.clientId,
          grant_type: GrantType.OneTimePin,
          otp: code,
          realm: mode,
          username,
          audience: this.audience,
          scope: this.scope,
        },
      );

      const {
        access_token: accessToken,
        refresh_token: refreshToken,
        id_token: idToken,
      } = data;
      const credentials: Credentials = { accessToken, idToken, refreshToken };
      await this.initializeSession(credentials);

      return credentials;
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }

  getUser(): PromiseOfOptional<T> {
    throw new Error('Method not implemented.');
  }

  async refreshTokens(): PromiseOfOptional<Credentials> {
    const storageKey = this.getStorageKey(AuthTokenName.RefreshToken);

    const refreshToken = await this.storage.getItem(storageKey);

    if (!refreshToken) {
      return undefined;
    }

    try {
      const path = `${this.baseURL}/oauth/token`;
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          client_id: this.clientId,
          grant_type: GrantType.RefreshToken,
          refresh_token: refreshToken,
          audience: this.audience,
          scope: this.scope,
        }),
      });

      if (!response.ok) {
        return undefined;
      }

      const data: AuthZeroTokenResponse = await response.json();

      const { access_token: accessToken, id_token: idToken } = data;

      const credentials: Credentials = { accessToken, idToken, refreshToken };
      if (this.autoRefresh) {
        await this.initializeSession(credentials);
      }

      return credentials;
    } catch (error) {
      throw new AuthZeroError(error);
    }
  }
}

export interface PasswordlessAuthZeroClientOptions {
  /**
   * The Auth0 Tenant domain
   */
  domain: string;
  /**
   * The client id belonging to your Auth0 Application
   */
  clientId: string;
}

interface AuthZeroTokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
}

export interface BaseAuthUser {
  email: string;
}
